import React, { useState, useCallback } from 'react';
import { FormGroup, Label, Button, Row, Col } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { Multiselect } from 'multiselect-react-dropdown';

import UrlFetcher from '../../containers/UrlFetcher';
import withSetModal, { TSetModalFunction } from '../../containers/WithSetModal';

import InputWithOptions from 'app/components/common/InputWithOptions';
import OrdersTable from 'app/components/orders/OrdersTable';
import { updateOrderStatus } from 'store/orders';
import { IOrder, IOrderWithSource } from 'types/order';
import { getArrayWithUpdatedObject } from 'utils/array';
import { selectIsLesznojeAdmin } from 'store/app';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { AnyRestaruant, OriginalRestaurant } from 'types';
import { downloadOrdersCSV } from 'utils/csv';
import { ORDER_STATUS } from 'enums';
import OtoFormGroup from 'app/components/common/OtoFormGroup';
import AdminRestaurantSelector from 'app/components/admin/AdminRestaurantSelector';

const selectOptions = [25, 50, 100, 200];

interface IOrdersDeclinationPageProps {
  orders: IOrderWithSource[];
  setData: Function;
  setModal: TSetModalFunction;
}

const OrdersDeclinationPage = (props: IOrdersDeclinationPageProps) => {
  const { orders, setData, setModal } = props;
  const dispatch = useAppDispatch();

  const onOrderDecline = useCallback(
    async (
      order: IOrder,
      status: IOrder['status'],
      restaurant: AnyRestaruant,
      extra
    ) => {
      //@TODO check if this works
      const updatedOrder = (await dispatch(
        updateOrderStatus(order, status, restaurant, extra)
      )) as unknown as IOrder;
      setData(getArrayWithUpdatedObject(orders, updatedOrder));
    },
    [orders, setData, dispatch]
  );

  const isLesznoAdmin = useAppSelector(selectIsLesznojeAdmin);

  const filteredOrders = isLesznoAdmin
    ? orders.filter((order) => order.source_rel.tenant === 'lesznoje')
    : orders;

  const handleDownloadCSV = useCallback(() => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const fileName = `zamowienia-${timestamp}.csv`;
    downloadOrdersCSV(filteredOrders, fileName);
  }, [filteredOrders]);

  return (
    <>
      <div className="mb-3">
        <Button 
          color="primary" 
          onClick={handleDownloadCSV}
          disabled={filteredOrders.length === 0}
        >
          Pobierz CSV ({filteredOrders.length} zamówień)
        </Button>
      </div>
      <OrdersTable
        orders={filteredOrders}
        onOrderDecline={onOrderDecline}
        setModal={setModal}
        showCustomer={true}
        showDeclinationIfPresent={true}
        showPaymentDetails={true}
        showRestaurant={true}
        showTenant={true}
      />
    </>
  );
};

const OrdersDeclinationPageWithFetchedData = (props: any) => {
  const [amountToFetch, setAmountToFetch] = useState<number | string>(50);
  const [createdAfter, setCreatedAfter] = useState<string>('');
  const [createdBefore, setCreatedBefore] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedRestaurants, setSelectedRestaurants] = useState<OriginalRestaurant[]>([]);
  const { promoCodeId } = useParams<{ promoCodeId?: string }>();
  
  // Order status options for multiselect
  const statusOptions = Object.values(ORDER_STATUS).map(status => ({
    name: status,
    label: status.charAt(0).toUpperCase() + status.slice(1)
  }));

  const getUrl = () => {
    const params = new URLSearchParams();
    params.append('amount', amountToFetch.toString());
    
    if (promoCodeId) {
      params.append('filter[promo_code_id]', promoCodeId);
    }
    
    if (createdAfter) {
      params.append('filter[created_after]', createdAfter);
    }
    
    if (createdBefore) {
      params.append('filter[created_before]', createdBefore);
    }
    
    if (selectedStatuses.length > 0) {
      params.append('filter[status]', selectedStatuses.join('|'));
    }

    if (selectedRestaurants.length > 0) {
      params.append('filter[restaurant_id]', selectedRestaurants.map(r => r.id).join('|'));
    }
    
    return `/admin/orders?${params.toString()}`;
  };

  return (
    <>
      <FormGroup>
        <Label for="rows">Ile zamówień pobrać</Label>
        <InputWithOptions
          options={selectOptions}
          className="mw-200"
          type="number"
          step="0.1"
          id="rows"
          name="rows"
          placeholder="Wpisz wartość"
          value={amountToFetch}
          onChange={(e) =>
            setAmountToFetch(
              e?.target?.value ? parseInt(e.target.value, 10) : ''
            )
          }
        />
      </FormGroup>
      <hr />
      <Row>
        <Col className="mw-300">
          <OtoFormGroup
            label="Data utworzenia od"
            type="date"
            name="createdAfter"
            value={createdAfter}
            onChange={(e) => setCreatedAfter(e.target.value)}
            className="mb-3"
          />
        </Col>
        <Col className="mw-300">
          <OtoFormGroup
            label="Data utworzenia do"
            type="date"
            name="createdBefore"
            value={createdBefore}
            onChange={(e) => setCreatedBefore(e.target.value)}
            className="mb-3"
          />
        </Col>
        <Col className="mw-500">
          <FormGroup>
            <Label>Status zamówienia</Label>
            <Multiselect
              options={statusOptions}
              selectedValues={statusOptions.filter(opt => selectedStatuses.includes(opt.name))}
              onSelect={(selectedList) => setSelectedStatuses(selectedList.map(item => item.name))}
              onRemove={(selectedList) => setSelectedStatuses(selectedList.map(item => item.name))}
              displayValue="label"
              placeholder="Wybierz statusy zamówień"
            />
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Label>Restauracja</Label>
            <AdminRestaurantSelector
              loadIfMissing={true}
              onSelect={(restaurant) => setSelectedRestaurants([restaurant])}
            />
          </FormGroup>
        </Col>
      </Row>
      
      <hr />
      {promoCodeId && <h1>Lista zamówień dla kodu promocyjnego: {promoCodeId}</h1>}
      <UrlFetcher url={getUrl()} delayBeforeRefresh={3000}>
        {([orders, urlFetcherProps]) => (
          <OrdersDeclinationPage
            orders={orders}
            {...urlFetcherProps}
            {...props}
          />
        )}
      </UrlFetcher>
    </>
  );
};

const EnhancedOrdersDeclinationPage = withSetModal(
  OrdersDeclinationPageWithFetchedData
);

EnhancedOrdersDeclinationPage.navName = 'admin.orders-declination';
EnhancedOrdersDeclinationPage.url = '/admin/orders-declination';

export default EnhancedOrdersDeclinationPage;
